import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardPen, ListChecks, Table2, ShieldCheck, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { CoEntryPanel } from "./CoEntryPanel";
import { RecordsView } from "./RecordsView";
import { coMessage } from "./coUtils";

const C = OPS.co;

const triggerClass =
    "gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-[color:var(--ink-muted)] transition-[color,background-color] duration-150 ease-out hover:text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)] data-[state=active]:bg-[color:var(--brand-orange)] data-[state=active]:text-white data-[state=active]:shadow-[var(--shadow-sm)]";

// Only built tabs exist (message 29 §3): an unknown tab key from the server is never rendered.
const TAB_DEFS = {
    entry: { icon: ClipboardPen, label: "co_tab_entry", render: (s) => <CoEntryPanel scope={s} mode="entry" /> },
    mine: { icon: ListChecks, label: "co_tab_mine", render: (s) => <CoEntryPanel scope={s} mode="mine" /> },
    records: { icon: Table2, label: "co_tab_records", render: (s) => <RecordsView scope={s} kind="records" /> },
    review: { icon: ShieldCheck, label: "co_tab_review", render: (s) => <RecordsView scope={s} kind="review" /> },
};

/**
 * «إلغاء الطلبات» inside the branch hub. The tab LIST is the server's
 * (`section.tabs` from `/branch-ops/scope`); the till data is `/co/scope`.
 * `ready: false` (pepper missing) and an empty branch scope are rendered as
 * notes — the form is never shown when a save could not succeed.
 */
export function CancellationsSection({ section }) {
    const { t, lang, dir } = useI18n();
    const [searchParams, setSearchParams] = useSearchParams();
    const [scope, setScope] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get("/co/scope")
            .then((r) => { if (alive) setScope(r.data); })
            .catch((e) => { if (alive) setError(e); })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const tabs = (section?.tabs || []).filter((k) => TAB_DEFS[k]);
    const requested = searchParams.get("tab");
    const active = tabs.includes(requested) ? requested
        : (tabs.includes(section?.default_tab) ? section.default_tab : tabs[0]);

    const onTabChange = (val) => {
        const next = new URLSearchParams(searchParams);
        next.set("tab", val);
        setSearchParams(next, { replace: true });
    };

    if (loading) return <InlineLoader label={t("loading")} />;

    let note = null;
    if (error) note = { id: C.notReady, text: coMessage(error, lang) || t("co_not_ready") };
    else if (!scope?.ready) note = { id: C.notReady, text: t("co_not_ready") };
    else if (scope.state === "assigned_branch_missing") note = { id: C.noHomeBranch, text: t("co_assigned_branch_missing") };
    else if (scope.state !== "ok") note = { id: C.noHomeBranch, text: t("co_no_home_branch") };

    return (
        <div data-testid={C.section} className="space-y-5">
            {note ? (
                <div data-testid={note.id}
                     className="flex items-start gap-2 rounded-[var(--radius-lg)] border border-dashed border-[color:var(--border-soft)] bg-[color:var(--bg-surface-2)] px-4 py-3">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--warning-soft-fg)]" />
                    <p className="text-sm font-semibold text-[color:var(--ink-muted)]">{note.text}</p>
                </div>
            ) : null}
            {tabs.length ? (
                <Tabs value={active} onValueChange={onTabChange} dir={dir} className="space-y-5">
                    <TabsList className="inline-flex h-auto w-full flex-wrap justify-start gap-1 rounded-full border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1 sm:w-auto">
                        {tabs.map((k) => {
                            const Icon = TAB_DEFS[k].icon;
                            return (
                                <TabsTrigger key={k} data-testid={C.tab(k)} value={k} className={triggerClass}>
                                    <Icon className="h-4 w-4" />
                                    {t(TAB_DEFS[k].label)}
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>
                    {tabs.map((k) => (
                        <TabsContent key={k} value={k} className="mt-0 focus-visible:outline-none">
                            {note ? null : TAB_DEFS[k].render(scope)}
                        </TabsContent>
                    ))}
                </Tabs>
            ) : null}
        </div>
    );
}
